export interface ApiError {
  message: string;
  errors?: string[];
  correlationId?: string;
}

/**
 * Extracts a user-friendly error message from various error types
 * Handles Axios errors, API responses, and standard Error objects
 */
export function getErrorMessage(error: unknown): string {
  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'object' && error !== null) {
    const apiError = error as any;
    
    // Check for axios response with error data
    if (apiError.response?.data) {
      const data = apiError.response.data;
      
      // Check for errors array first (our API format)
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors.join(', ');
      }
      
      // Check for message field
      if (data.message && typeof data.message === 'string') {
        return data.message;
      }
      
      // Check for error field (sometimes APIs use 'error' instead of 'message')
      if (data.error && typeof data.error === 'string') {
        return data.error;
      }
    }
    
    // Handle axios error without response (network errors)
    if (apiError.request && !apiError.response) {
      return 'Network error. Please check your connection and try again.';
    }
    
    // Fallback to error message property
    if (apiError.message && typeof apiError.message === 'string') {
      return apiError.message;
    }
  }
  
  return 'An unexpected error occurred';
}

/**
 * Handles API errors and returns a user-friendly message
 * Falls back to the provided fallback message if extraction fails
 */
export function handleApiError(error: unknown, fallbackMessage: string = 'An error occurred'): string {
  const message = getErrorMessage(error);
  return message && message !== 'An unexpected error occurred' ? message : fallbackMessage;
}
